import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Switch,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  ExpandMore,
  AccountBalance
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

// Separate component to isolate dialog re-renders from main component
const EditOrgDialog = React.memo(({ open, orgName, onClose, onSave }) => {
  const [name, setName] = useState('');

  // Set initial value when dialog opens
  React.useEffect(() => {
    if (open && orgName) {
      setName(orgName);
    }
  }, [open, orgName]);

  const handleSave = () => {
    if (!name.trim()) {
      alert('Channel name is required');
      return;
    }
    if (name.trim() === orgName) {
      onClose();
      return;
    }
    onSave(orgName, name.trim());
    setName('');
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Channel</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Channel Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
});

const AddOrgDialog = React.memo(({ open, onClose, onSave }) => {
  const [name, setName] = useState('');

  const handleSave = () => {
    if (!name.trim()) {
      alert('Channel name is required');
      return;
    }
    onSave(name.trim());
    setName('');
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Channel</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Channel Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="e.g., Rise, Good Day Farms"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Create</Button>
      </DialogActions>
    </Dialog>
  );
});

// Extracted + memoized so typing in the form does NOT re-render the (large) Channels
// tree on every keystroke — that re-render was the input lag. Local form state lives
// here; the parent only controls open/mode and the initial values.
const AliasDialog = React.memo(function AliasDialog({ open, mode, initial, storeOptions, onClose, onSave }) {
  const [settlementName, setSettlementName] = useState('');
  const [canonicalName, setCanonicalName] = useState('');
  const [notes, setNotes] = useState('');

  // Seed the form when the dialog opens (initial is stable while open).
  useEffect(() => {
    if (open) {
      setSettlementName(initial?.settlementName || '');
      setCanonicalName(initial?.canonicalName || '');
      setNotes(initial?.notes || '');
    }
  }, [open, initial]);

  const canSave = settlementName.trim() && canonicalName.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'add' ? 'Add Location Alias' : 'Edit Location Alias'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Settlement Name"
          fullWidth
          value={settlementName}
          onChange={(e) => setSettlementName(e.target.value)}
          helperText="The name as it appears in settlement data"
        />
        <Autocomplete
          freeSolo
          options={storeOptions || []}
          inputValue={canonicalName}
          onInputChange={(e, v) => setCanonicalName(v || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Maps To (Store)"
              helperText="Pick the store this maps to (type to filter)"
            />
          )}
        />
        <TextField
          margin="dense"
          label="Notes"
          fullWidth
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSave({ settlementName: settlementName.trim(), canonicalName: canonicalName.trim(), notes })}
          variant="contained"
          disabled={!canSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// Search any location and place/re-assign it under an existing Channel -> Market.
// Self-contained (its own state) so typing/searching doesn't re-render the heavy hierarchy
// accordions below. Reuses the channel-management endpoints: search-locations + hierarchy-options
// + place-location.
const AssignLocationPanel = React.memo(({ apiCall, onAssigned }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [channel, setChannel] = useState('');
  const [market, setMarket] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [msg, setMsg] = useState(null);
  const reqRef = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall('/api/v1/channels/stores?view=hierarchy-options', 'GET');
        setHierarchyOptions(res.options || []);
      } catch (e) { console.error('hierarchy-options load failed:', e); }
    })();
  }, [apiCall]);

  // Debounced, stale-protected location search.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    const id = ++reqRef.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiCall(`/api/v1/channels/stores?view=search-locations&q=${encodeURIComponent(term)}`, 'GET');
        if (id === reqRef.current) setResults(res.locations || []);
      } catch (e) {
        if (id === reqRef.current) setResults([]);
      } finally {
        if (id === reqRef.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, apiCall]);

  const channelOptions = [...new Set(hierarchyOptions.map(o => o.channel).filter(Boolean))].sort();
  const marketOptions = [...new Set(hierarchyOptions.filter(o => !channel || o.channel === channel).map(o => o.market).filter(Boolean))].sort();

  const selectLocation = (loc) => {
    setSelected(loc);
    setMsg(null);
    setChannel(loc?.assigned ? (loc.channel || '') : '');
    setMarket(loc?.assigned ? (loc.market || '') : '');
  };

  const handleAssign = async () => {
    if (!selected || !channel || !market) {
      setMsg({ type: 'error', text: 'Pick a location and a Channel / Market.' });
      return;
    }
    setAssigning(true); setMsg(null);
    try {
      await apiCall('/api/v1/channels/stores', 'POST', {
        action: 'place-location', store: selected.name, channel, market
      });
      setMsg({ type: 'success', text: `Assigned "${selected.name}" to ${channel} / ${market}.` });
      setSelected(null); setQuery(''); setResults([]);
      if (onAssigned) onAssigned();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Assignment failed' });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Assign a Location to a Channel</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search any location and place it under an existing Channel / Market. Placing a
          location registers it in the hierarchy and bridges its terminals.
        </Typography>
        <Autocomplete
          options={results}
          getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name || ''))}
          filterOptions={(x) => x}
          loading={searching}
          noOptionsText={query.trim().length < 2 ? 'Type 2+ characters…' : 'No matching locations'}
          onInputChange={(e, v, reason) => { if (reason === 'input') setQuery(v); }}
          onChange={(e, v) => selectLocation(v && typeof v === 'object' ? v : null)}
          isOptionEqualToValue={(o, v) => o.name === v?.name}
          renderOption={(props, o) => (
            <li {...props} key={o.name}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                <span>{o.name}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">({o.terminalCount} terminals)</Typography>
                </span>
                {o.assigned
                  ? <Chip size="small" label={`${o.channel} / ${o.market}`} />
                  : <Chip size="small" color="warning" label="Unassigned" />}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search locations" placeholder="Type 2+ characters…"
              InputProps={{ ...params.InputProps, endAdornment: (<>{searching ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }} />
          )}
        />
        {selected && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Place <strong>{selected.name}</strong>{' '}
              {selected.assigned
                ? <Typography component="span" variant="caption" color="text.secondary">(currently {selected.channel} / {selected.market})</Typography>
                : <Chip size="small" color="warning" label="currently Unassigned" />}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Autocomplete sx={{ minWidth: 200 }} options={channelOptions} value={channel || null}
                onChange={(e, v) => { setChannel(v || ''); setMarket(''); }}
                renderInput={(p) => <TextField {...p} label="Channel" />} />
              <Autocomplete sx={{ minWidth: 200 }} options={marketOptions} value={market || null}
                onChange={(e, v) => setMarket(v || '')}
                renderInput={(p) => <TextField {...p} label="Market" />} />
              <Button variant="contained" onClick={handleAssign} disabled={assigning || !channel || !market}>
                {assigning ? 'Assigning…' : 'Assign'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The Channel / Market must already exist (create it above with "Add Market" first).
            </Typography>
          </Box>
        )}
        {msg && <Alert severity={msg.type} sx={{ mt: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </CardContent>
    </Card>
  );
});

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box role="tabpanel" sx={{ pt: 1 }}>{children}</Box>;
}

// ---------------------------------------------------------------------------
// Partner Groups tab — manage define-once cross-cutting terminal sets (e.g. the
// Flowhub POS partner). Admin-only (this whole page is admin-gated). Self-service:
// create a group, add terminals or whole locations to it, see the roster, and
// assign users. All calls go to /api/v1/channels/partner-groups.
// ---------------------------------------------------------------------------
const PartnerGroupsPanel = React.memo(function PartnerGroupsPanel({ apiCall }) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ groupName: '', description: '' });

  const [rosterPage, setRosterPage] = useState(0); // 0-based for TablePagination
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterSearchInput, setRosterSearchInput] = useState('');

  const [termQuery, setTermQuery] = useState('');
  const [termResults, setTermResults] = useState([]);
  const [termSelected, setTermSelected] = useState([]);
  const [termSearching, setTermSearching] = useState(false);

  const [locResults, setLocResults] = useState([]);
  const [locSearching, setLocSearching] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const r = await apiCall('/api/v1/channels/partner-groups', 'GET');
      setGroups(r.partnerGroups || []);
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to load partner groups' }); }
    finally { setLoadingGroups(false); }
  }, [apiCall]);

  const loadDetail = useCallback(async (name, page = 0, q = '') => {
    if (!name) return;
    setDetailLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page + 1), limit: '50' });
      if (q) qs.set('q', q);
      const r = await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(name)}?${qs.toString()}`, 'GET');
      setDetail(r);
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to load group detail' }); }
    finally { setDetailLoading(false); }
  }, [apiCall]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { if (selected) loadDetail(selected, rosterPage, rosterSearch); }, [selected, rosterPage, rosterSearch, loadDetail]);

  // Debounced terminal typeahead
  useEffect(() => {
    if (termQuery.trim().length < 2) { setTermResults([]); return; }
    setTermSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiCall(`/api/v1/channels/partner-groups?search=terminals&q=${encodeURIComponent(termQuery.trim())}`, 'GET');
        setTermResults(r.terminals || []);
      } catch { /* ignore */ } finally { setTermSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [termQuery, apiCall]);

  const searchLocations = useCallback(async (q) => {
    if (q.trim().length < 2) { setLocResults([]); return; }
    setLocSearching(true);
    try {
      const r = await apiCall(`/api/v1/channels/partner-groups?search=locations&q=${encodeURIComponent(q.trim())}`, 'GET');
      setLocResults(r.locations || []);
    } catch { /* ignore */ } finally { setLocSearching(false); }
  }, [apiCall]);

  const refreshAll = async () => { await loadGroups(); if (selected) await loadDetail(selected, rosterPage, rosterSearch); };

  const doCreate = async () => {
    if (!newGroup.groupName.trim()) return;
    setBusy(true);
    try {
      await apiCall('/api/v1/channels/partner-groups', 'POST', newGroup);
      setCreateOpen(false); setNewGroup({ groupName: '', description: '' });
      await loadGroups(); setMsg({ type: 'success', text: 'Partner group created' });
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to create group' }); }
    finally { setBusy(false); }
  };

  const addTerminals = async () => {
    const ids = termSelected.map(t => (typeof t === 'string' ? t : t.terminalId)).filter(Boolean);
    if (!ids.length) { setMsg({ type: 'warning', text: 'Type or pick at least one terminal' }); return; }
    setBusy(true);
    try {
      const r = await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(selected)}`, 'POST', { action: 'add-terminals', terminals: ids });
      setMsg({ type: 'success', text: `Added ${r.inserted} form(s) (${r.formsResolved} resolved from ${r.requested} entered)` });
      setTermSelected([]); setTermQuery(''); setTermResults([]); setRosterPage(0);
      await refreshAll();
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to add terminals' }); }
    finally { setBusy(false); }
  };

  const addLocation = async (loc) => {
    if (!loc) return;
    setBusy(true);
    try {
      const r = await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(selected)}`, 'POST', { action: 'add-location', location: loc });
      setMsg({ type: 'success', text: `Added ${r.inserted} form(s) from "${loc}" (${r.seedTerminals} terminals)` });
      setLocResults([]); setRosterPage(0);
      await refreshAll();
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to add location' }); }
    finally { setBusy(false); }
  };

  const removeTerminal = async (physicalKey) => {
    setBusy(true);
    try {
      await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(selected)}`, 'POST', { action: 'remove-terminal', physicalKey });
      await refreshAll();
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to remove terminal' }); }
    finally { setBusy(false); }
  };

  const addUser = async () => {
    if (!userEmail.trim()) return;
    setBusy(true);
    try {
      await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(selected)}`, 'POST', { action: 'add-user', email: userEmail.trim() });
      setUserEmail(''); setMsg({ type: 'success', text: 'User added to group' });
      await refreshAll();
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to add user' }); }
    finally { setBusy(false); }
  };

  const removeUser = async (userId) => {
    setBusy(true);
    try {
      await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(selected)}`, 'POST', { action: 'remove-user', userId });
      await refreshAll();
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to remove user' }); }
    finally { setBusy(false); }
  };

  const toggleActive = async (g) => {
    try {
      await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(g.groupName)}`, 'PUT', { isActive: !g.isActive });
      await loadGroups();
      if (selected === g.groupName) await loadDetail(selected, rosterPage, rosterSearch);
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to toggle active' }); }
  };

  const deleteGroup = async (name) => {
    if (!window.confirm(`Delete partner group "${name}"? This removes the group, its terminal roster, and all user grants. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await apiCall(`/api/v1/channels/partner-groups/${encodeURIComponent(name)}`, 'DELETE');
      if (selected === name) { setSelected(null); setDetail(null); }
      await loadGroups(); setMsg({ type: 'success', text: 'Partner group deleted' });
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Failed to delete group' }); }
    finally { setBusy(false); }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Partner Groups are define-once, cross-cutting terminal sets (e.g. a POS partner on revenue-share).
          Grant the group to a user and they see every terminal in it — no matter which channel or store it lives under.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<Refresh />} onClick={loadGroups}>Refresh</Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>New Group</Button>
        </Box>
      </Box>
      {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Grid container spacing={2}>
        {/* Left: group list */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Groups</Typography>
              {loadingGroups ? <CircularProgress size={22} /> : (
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Terminals</TableCell>
                        <TableCell align="right">Users</TableCell>
                        <TableCell align="center">Active</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groups.length === 0 && (
                        <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No partner groups yet. Click “New Group”.</Typography></TableCell></TableRow>
                      )}
                      {groups.map((g) => (
                        <TableRow key={g.groupName} hover selected={selected === g.groupName}
                          sx={{ cursor: 'pointer' }} onClick={() => { setSelected(g.groupName); setRosterPage(0); setRosterSearch(''); setRosterSearchInput(''); }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={selected === g.groupName ? 700 : 400}>{g.groupName}</Typography>
                            {g.description && <Typography variant="caption" color="text.secondary">{g.description}</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`${g.formCount} ID forms`}><span>{g.physicalCount}</span></Tooltip>
                          </TableCell>
                          <TableCell align="right">{g.userCount}</TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Switch size="small" checked={!!g.isActive} onChange={() => toggleActive(g)} color="success" />
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Delete group"><IconButton size="small" onClick={() => deleteGroup(g.groupName)}><Delete fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: selected group detail */}
        <Grid item xs={12} md={8}>
          {!selected ? (
            <Card variant="outlined"><CardContent><Typography color="text.secondary">Select a group on the left to manage its terminals and users.</Typography></CardContent></Card>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">{selected}
                    {detail && <Chip size="small" sx={{ ml: 1 }} label={detail.group.isActive ? 'Active' : 'Inactive'} color={detail.group.isActive ? 'success' : 'default'} />}
                  </Typography>
                  {detail && <Typography variant="body2" color="text.secondary">{detail.pagination.physicalTotal} terminals · {detail.pagination.formTotal} ID forms · {detail.members.length} users</Typography>}
                </Box>
                {detailLoading && !detail ? <CircularProgress size={22} /> : detail && (
                  <>
                    {/* Add terminals */}
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Add terminals</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                      <Autocomplete
                        multiple freeSolo size="small" sx={{ flex: 1 }}
                        options={termResults}
                        filterOptions={(x) => x}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.terminalId}${o.store ? ' — ' + o.store : ''}`)}
                        value={termSelected}
                        onChange={(e, v) => setTermSelected(v)}
                        onInputChange={(e, v) => setTermQuery(v)}
                        loading={termSearching}
                        renderInput={(params) => (
                          <TextField {...params} label="Terminal IDs (type an ID, e.g. 01001001 or MP003988; Enter to add)"
                            helperText="Numeric or letter form — both counterparts are added automatically"
                            InputProps={{ ...params.InputProps, endAdornment: (<>{termSearching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                        )}
                      />
                      <Button variant="contained" disabled={busy || !termSelected.length} onClick={addTerminals} sx={{ mt: 0.5 }}>Add</Button>
                    </Box>

                    {/* Add whole location */}
                    <Typography variant="subtitle2">Add a whole location</Typography>
                    <Autocomplete
                      size="small" sx={{ mb: 2 }}
                      options={locResults}
                      filterOptions={(x) => x}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                      loading={locSearching}
                      onInputChange={(e, v) => searchLocations(v)}
                      onChange={(e, v) => { if (v && v.name) addLocation(v.name); }}
                      renderOption={(props, o) => (
                        <li {...props} key={o.name}><ListItemText primary={o.name} secondary={`${o.terminalCount} terminals${o.market ? ' · ' + o.market : ''}`} /></li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Search a location to add all its terminals"
                          InputProps={{ ...params.InputProps, endAdornment: (<>{locSearching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                      )}
                    />

                    <Divider sx={{ my: 1 }} />

                    {/* Roster */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Roster ({detail.pagination.physicalTotal} terminals)</Typography>
                      <TextField size="small" placeholder="Filter roster…" value={rosterSearchInput}
                        onChange={(e) => setRosterSearchInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setRosterPage(0); setRosterSearch(rosterSearchInput.trim()); } }} />
                    </Box>
                    <TableContainer sx={{ maxHeight: 340, mt: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Terminal</TableCell>
                            <TableCell>ID forms</TableCell>
                            <TableCell>Added via</TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detail.roster.length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No terminals yet — add some above.</Typography></TableCell></TableRow>
                          )}
                          {detail.roster.map((r) => (
                            <TableRow key={r.physicalKey} hover>
                              <TableCell><Typography variant="body2" fontWeight={600}>{r.physicalKey}</Typography></TableCell>
                              <TableCell>{(r.forms || []).map((f) => <Chip key={f} size="small" label={f} sx={{ mr: 0.5, mb: 0.5 }} />)}</TableCell>
                              <TableCell><Typography variant="caption" color="text.secondary">{r.addedVia || '—'}</Typography></TableCell>
                              <TableCell align="right"><Tooltip title="Remove terminal"><IconButton size="small" disabled={busy} onClick={() => removeTerminal(r.physicalKey)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination component="div" count={detail.pagination.physicalTotal}
                      page={rosterPage} rowsPerPage={50} rowsPerPageOptions={[50]}
                      onPageChange={(e, p) => setRosterPage(p)} />

                    <Divider sx={{ my: 1 }} />

                    {/* Users */}
                    <Typography variant="subtitle2">Users with access ({detail.members.length})</Typography>
                    <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                      <TextField size="small" sx={{ flex: 1 }} label="Add user by email"
                        value={userEmail} onChange={(e) => setUserEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addUser(); }} />
                      <Button variant="outlined" disabled={busy || !userEmail.trim()} onClick={addUser}>Add user</Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 240 }}>
                      <Table size="small" stickyHeader>
                        <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Name</TableCell><TableCell>Role</TableCell><TableCell align="right" /></TableRow></TableHead>
                        <TableBody>
                          {detail.members.length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No users assigned yet.</Typography></TableCell></TableRow>
                          )}
                          {detail.members.map((m) => (
                            <TableRow key={m.userId} hover>
                              <TableCell>{m.email}</TableCell>
                              <TableCell>{[m.firstName, m.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                              <TableCell><Chip size="small" label={m.role} /></TableCell>
                              <TableCell align="right"><Tooltip title="Remove user"><IconButton size="small" disabled={busy} onClick={() => removeUser(m.userId)}><Delete fontSize="small" /></IconButton></Tooltip></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create group dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Partner Group</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Group name" value={newGroup.groupName}
            onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })} />
          <TextField fullWidth margin="dense" label="Description (optional)" value={newGroup.description}
            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !newGroup.groupName.trim()} onClick={doCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// Display-only helper: the market-tier catch-all is stored as 'Other' but should read
// 'Unassigned' to the user. Never use this for API payloads/filters — only render sites.
const displayMarket = (m) => (m === 'Other' ? 'Unassigned' : m);

const Channels = () => {
  const { apiCall, getAccounts } = useApi();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const hasAutoExpanded = useRef(false);
  const [expandedChannel, setExpandedChannel] = useState(null);
  const [expandedStores, setExpandedStores] = useState({}); // { storeName: true/false }
  const [storeTerminals, setStoreTerminals] = useState({}); // { storeName: [terminals] }
  const [loadingStoreTerminals, setLoadingStoreTerminals] = useState({}); // { storeName: true/false }

  // Data states
  const [summary, setSummary] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelsByOrg, setChannelsByOrg] = useState({}); // { orgName: [channels] }
  const [channelDetails, setChannelDetails] = useState({}); // { channelName: { stores, aliases } }
  const [organizations, setOrganizations] = useState([]); // List of org names for dropdowns
  const [allStores, setAllStores] = useState([]); // List of all stores for terminal reassignment
  const [allStoreNames, setAllStoreNames] = useState([]); // Combined store names from all sources

  // Available accounts for selection
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loadingAvailableAccounts, setLoadingAvailableAccounts] = useState(false);

  // Dialog states
  const [channelDialog, setChannelDialog] = useState({ open: false, mode: 'add', data: {} });
  const [storeDialog, setStoreDialog] = useState({ open: false, mode: 'add', data: {}, channel: '' });
  const [aliasDialog, setAliasDialog] = useState({ open: false, mode: 'add', data: {}, store: '' });
  const [terminalDialog, setTerminalDialog] = useState({ open: false, terminal: null, newStore: '', channel: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '', channel: '' });
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [editOrgDialog, setEditOrgDialog] = useState({ open: false, orgName: '' });

  // Store/Location account states
  const [storeAccounts, setStoreAccounts] = useState({}); // { storeName: [accounts] }
  const [accountDialog, setAccountDialog] = useState({ open: false, selectedAccounts: [], store: '' });

  // Load summary stats
  const loadSummary = useCallback(async () => {
    try {
      const response = await apiCall('/api/v1/channels/summary', 'GET');
      setSummary(response);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, [apiCall]);

  // Load organizations for dropdowns (fetches ALL orgs including empty ones)
  const loadOrganizations = useCallback(async () => {
    try {
      const response = await apiCall('/api/v1/channels/organizations', 'GET');
      const orgNames = (response.organizations || []).map(o => o.name);
      // Add 'Unassigned' if not present and sort
      if (!orgNames.includes('Unassigned')) {
        orgNames.push('Unassigned');
      }
      orgNames.sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });
      setOrganizations(orgNames);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  }, [apiCall]);

  // Load channels and group by organization
  const loadChannels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/v1/channels', 'GET');
      const allChannels = response.channels || [];
      setChannels(allChannels);

      // Group channels by organization
      const grouped = {};
      allChannels.forEach(channel => {
        const org = channel.organization || 'Unassigned';
        if (!grouped[org]) {
          grouped[org] = [];
        }
        grouped[org].push(channel);
      });
      setChannelsByOrg(grouped);

      // Auto-expand first org only on initial load
      if (!hasAutoExpanded.current) {
        const orgs = Object.keys(grouped).sort((a, b) => {
          if (a === 'Unassigned') return 1;
          if (b === 'Unassigned') return -1;
          return a.localeCompare(b);
        });
        if (orgs.length > 0) {
          setExpandedOrg(orgs[0]);
          hasAutoExpanded.current = true;
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Load channel details (stores, aliases, terminals) when expanded
  const loadChannelDetails = useCallback(async (channelName, orgName) => {
    try {
      // Load stores for this channel, scoped to organization
      const storesResponse = await apiCall('/api/v1/channels/stores', 'GET');
      const channelStores = (storesResponse.stores || []).filter(s =>
        s.channel === channelName && (!orgName || s.organization === orgName)
      );

      // Load aliases that map to stores in this channel (scoped by org via storeNames)
      const aliasesResponse = await apiCall('/api/v1/channels/aliases', 'GET');
      const storeNames = channelStores.map(s => s.name);
      const channelAliases = (aliasesResponse.aliases || []).filter(a =>
        storeNames.includes(a.canonical_name)
      );

      // Load terminals for stores in this channel (scoped by org via storeNames)
      const terminalsResponse = await apiCall('/api/v1/channels/terminal-assignments', 'GET');
      const channelTerminals = (terminalsResponse.terminals || []).filter(t =>
        storeNames.includes(t.store)
      );

      const key = `${orgName || ''}:${channelName}`;
      setChannelDetails(prev => ({
        ...prev,
        [key]: {
          stores: channelStores,
          aliases: channelAliases,
          terminals: channelTerminals
        }
      }));
    } catch (err) {
      console.error(`Failed to load details for channel ${channelName}:`, err);
    }
  }, [apiCall]);

  // Load all stores for terminal reassignment dropdown
  const loadAllStores = useCallback(async () => {
    try {
      const [channelStoresResp, businessesResp] = await Promise.all([
        apiCall('/api/v1/channels/stores', 'GET'),
        apiCall('/api/v1/businesses', 'GET').catch(() => [])
      ]);
      const channelStores = channelStoresResp.stores || [];
      setAllStores(channelStores);

      // Combine store names from location_hierarchy AND settlement_deposits for autocomplete
      const names = new Set(channelStores.map(s => s.name));
      (Array.isArray(businessesResp) ? businessesResp : []).forEach(b => {
        if (b.LocationName) names.add(b.LocationName);
      });
      setAllStoreNames([...names].sort());
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  }, [apiCall]);

  // Load available accounts for assignment dropdown
  const loadAvailableAccounts = useCallback(async () => {
    setLoadingAvailableAccounts(true);
    try {
      const response = await getAccounts();
      setAvailableAccounts(response || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      // Set some fallback accounts
      setAvailableAccounts([]);
    } finally {
      setLoadingAvailableAccounts(false);
    }
  }, [getAccounts]);

  // Initial load
  useEffect(() => {
    loadSummary();
    loadChannels();
    loadAllStores();
    loadOrganizations();
    loadAvailableAccounts();
  }, [loadSummary, loadChannels, loadAllStores, loadOrganizations, loadAvailableAccounts]);

  // Load channel details when expanded (expandedChannel is now "org:channel" key)
  useEffect(() => {
    if (expandedChannel && !channelDetails[expandedChannel]) {
      const [orgName, channelName] = expandedChannel.split(':');
      loadChannelDetails(channelName, orgName);
    }
  }, [expandedChannel, channelDetails, loadChannelDetails]);

  // Load terminals for a specific store
  const loadStoreTerminals = useCallback(async (storeName) => {
    if (storeTerminals[storeName]) return; // Already loaded

    setLoadingStoreTerminals(prev => ({ ...prev, [storeName]: true }));
    try {
      const response = await apiCall(`/api/v1/channels/terminal-assignments?store=${encodeURIComponent(storeName)}`, 'GET');
      setStoreTerminals(prev => ({
        ...prev,
        [storeName]: response.terminals || []
      }));
    } catch (err) {
      console.error(`Failed to load terminals for store ${storeName}:`, err);
    } finally {
      setLoadingStoreTerminals(prev => ({ ...prev, [storeName]: false }));
    }
  }, [apiCall, storeTerminals]);

  // Load accounts assigned to a store/location
  const loadStoreAccounts = useCallback(async (storeName, forceReload = false) => {
    if (!forceReload && storeAccounts[storeName]) return; // Already loaded
    try {
      const response = await apiCall(`/api/v1/channels/location-accounts/${encodeURIComponent(storeName)}`, 'GET');
      setStoreAccounts(prev => ({
        ...prev,
        [storeName]: response.accounts || []
      }));
    } catch (err) {
      console.error(`Failed to load accounts for store ${storeName}:`, err);
    }
  }, [apiCall, storeAccounts]);

  // Handle store expansion
  const handleStoreToggle = (storeName) => {
    const isExpanding = !expandedStores[storeName];
    setExpandedStores(prev => ({ ...prev, [storeName]: isExpanding }));
    if (isExpanding) {
      loadStoreTerminals(storeName);
      loadStoreAccounts(storeName);
    }
  };

  // Handle organization accordion change
  const handleOrgAccordionChange = (orgName) => (event, isExpanded) => {
    setExpandedOrg(isExpanded ? orgName : null);
  };

  // Handle channel accordion change - use org:channel composite key
  const handleAccordionChange = (channelName, orgName) => (event, isExpanded) => {
    const key = `${orgName}:${channelName}`;
    setExpandedChannel(isExpanded ? key : null);
  };

  // Refresh current channel details - accepts "org:channel" composite key or separate args
  const refreshChannelDetails = async (channelOrKey, orgName) => {
    let channel = channelOrKey;
    let org = orgName;
    if (!orgName && channelOrKey && channelOrKey.includes(':')) {
      [org, channel] = channelOrKey.split(':');
    }
    if (channel) {
      await loadChannelDetails(channel, org);
    }
    await loadChannels();
    await loadSummary();
    await loadAllStores();
  };

  // Organization CRUD handler
  const handleSaveOrg = useCallback(async (orgName) => {
    try {
      await apiCall('/api/v1/channels/organizations', 'POST', {
        name: orgName
      });
      setOrgDialogOpen(false);
      await loadChannels();
      await loadOrganizations();
    } catch (err) {
      alert(err.message || 'Failed to create channel');
    }
  }, [apiCall, loadChannels, loadOrganizations]);

  // Edit organization handler
  const handleEditOrg = useCallback(async (oldName, newName) => {
    try {
      await apiCall('/api/v1/channels/organizations', 'PUT', {
        oldName: oldName,
        name: newName
      });
      setEditOrgDialog({ open: false, orgName: '' });
      await loadChannels();
      await loadOrganizations();
    } catch (err) {
      alert(err.message || 'Failed to rename channel');
    }
  }, [apiCall, loadChannels, loadOrganizations]);

  // Channel CRUD handlers
  const handleSaveChannel = async () => {
    console.log('handleSaveChannel called', channelDialog);
    try {
      const { mode, data } = channelDialog;
      console.log('Mode:', mode, 'Data:', data);
      if (mode === 'add') {
        await apiCall('/api/v1/channels', 'POST', {
          name: data.name,
          organization: data.organization,
          type: data.type
        });
      } else {
        console.log('Calling PUT for:', data.originalName, 'with org:', data.organization);
        await apiCall(`/api/v1/channels/${encodeURIComponent(data.originalName)}`, 'PUT', {
          name: data.name,
          organization: data.organization
        });
      }
      setChannelDialog({ open: false, mode: 'add', data: {} });
      await loadChannels();
      await loadSummary();
    } catch (err) {
      console.error('handleSaveChannel error:', err);
      alert(err.message || 'Failed to save market');
    }
  };

  // Store CRUD handlers
  const handleSaveStore = async () => {
    try {
      const { mode, data, channel } = storeDialog;
      if (mode === 'add') {
        await apiCall('/api/v1/channels/stores', 'POST', {
          name: data.name,
          channel: channel,
          organization: data.organization,
          type: data.type,
          subLocation: data.subLocation
        });
      } else {
        await apiCall(`/api/v1/channels/stores/${data.id}`, 'PUT', {
          name: data.name,
          channel: channel,
          organization: data.organization,
          type: data.type,
          subLocation: data.subLocation
        });
      }
      const org = data.organization || '';
      setStoreDialog({ open: false, mode: 'add', data: {}, channel: '' });
      await refreshChannelDetails(channel, org);
      await loadChannels();
      await loadSummary();
    } catch (err) {
      alert(err.message || 'Failed to save store');
    }
  };

  // Alias CRUD handlers
  const handleSaveAlias = async (values) => {
    try {
      const { mode, data } = aliasDialog;
      if (mode === 'add') {
        await apiCall('/api/v1/channels/aliases', 'POST', {
          settlementName: values.settlementName,
          canonicalName: values.canonicalName,
          matchType: data.matchType || 'exact',
          notes: values.notes
        });
      } else {
        await apiCall(`/api/v1/channels/aliases/${data.id}`, 'PUT', {
          settlementName: values.settlementName,
          canonicalName: values.canonicalName,
          matchType: data.matchType,
          notes: values.notes
        });
      }
      setAliasDialog({ open: false, mode: 'add', data: {}, store: '' });
      if (expandedChannel) {
        await refreshChannelDetails(expandedChannel);
      }
    } catch (err) {
      alert(err.message || 'Failed to save alias');
    }
  };

  // Terminal reassignment handler
  const handleReassignTerminal = async () => {
    try {
      await apiCall(`/api/v1/channels/terminal-assignments/${terminalDialog.terminal}`, 'PUT', {
        store: terminalDialog.newStore
      });
      setTerminalDialog({ open: false, terminal: null, newStore: '', channel: '' });
      if (terminalDialog.channel) {
        await refreshChannelDetails(terminalDialog.channel);
      }
    } catch (err) {
      alert(err.message || 'Failed to reassign terminal');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      const { type, id, channel, store } = deleteDialog;
      await apiCall(`/api/v1/channels/${type}/${encodeURIComponent(id)}`, 'DELETE');
      setDeleteDialog({ open: false, type: '', id: '', name: '', channel: '', store: '' });

      if (type === 'channels') {
        loadChannels();
        loadSummary();
      } else if (type === 'location-accounts' && store) {
        // Force reload accounts for this store
        loadStoreAccounts(store, true);
      } else if (channel) {
        await refreshChannelDetails(channel);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  // Store/Location account assignment handler - saves multiple accounts
  const handleSaveStoreAccount = async () => {
    try {
      const { selectedAccounts, store } = accountDialog;

      // Save each selected account
      for (const account of selectedAccounts) {
        const accountId = account.accountId || account.account_id || account.rta;
        await apiCall('/api/v1/channels/location-accounts', 'POST', {
          locationName: store,
          accountIdentifier: accountId
        });
      }

      setAccountDialog({ open: false, selectedAccounts: [], store: '' });
      // Force reload accounts for this store
      loadStoreAccounts(store, true);
    } catch (err) {
      alert(err.message || 'Failed to save account assignment');
    }
  };

  // Render summary cards
  const renderSummary = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={2.4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{new Set([...Object.keys(channelsByOrg), ...organizations.filter(o => o !== 'Unassigned')]).size || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Channels</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={2.4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{summary?.channels || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Markets</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={2.4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{summary?.stores || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Stores</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={2.4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{summary?.aliases || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Aliases</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={2.4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{summary?.terminals || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Terminals</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render stores section within a channel - each store is expandable to show terminals
  const renderStoresSection = (channelName, stores, orgName) => (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Stores ({stores.length})
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setStoreDialog({ open: true, mode: 'add', data: { organization: orgName || '' }, channel: channelName })}
        >
          Add Store
        </Button>
      </Box>
      {stores.length > 0 ? (
        <Box>
          {stores.map((store) => (
            <Accordion
              key={store.id}
              expanded={expandedStores[store.name] || false}
              onChange={() => handleStoreToggle(store.name)}
              sx={{
                mb: 0.5,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ backgroundColor: 'grey.50', '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Typography sx={{ flexGrow: 1, fontWeight: 'medium' }}>{store.name}</Typography>
                  <Chip
                    label={`${store.terminal_count || 0} terminals`}
                    size="small"
                    color={store.terminal_count > 0 ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ mr: 2 }}
                  />
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit Store">
                      <IconButton
                        size="small"
                        onClick={() => setStoreDialog({
                          open: true,
                          mode: 'edit',
                          data: store,
                          channel: channelName
                        })}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Store">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({
                          open: true,
                          type: 'stores',
                          id: store.id,
                          name: store.name,
                          channel: channelName
                        })}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                {/* Terminals Section */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Terminals
                </Typography>
                {loadingStoreTerminals[store.name] ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : storeTerminals[store.name]?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell><strong>Terminal ID</strong></TableCell>
                          <TableCell><strong>Normalized ID</strong></TableCell>
                          <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {storeTerminals[store.name].map((terminal) => (
                          <TableRow key={terminal.terminal_id} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{terminal.terminal_id}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{terminal.normalized_terminal_id || '-'}</TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setTerminalDialog({
                                  open: true,
                                  terminal: terminal.terminal_id,
                                  newStore: terminal.store || '',
                                  channel: channelName
                                })}
                              >
                                Reassign
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No terminals assigned to this store.
                  </Typography>
                )}

                {/* Bank Accounts Section */}
                {renderAccountsSection(store.name)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          No stores in this channel yet.
        </Typography>
      )}
    </Box>
  );

  // Render aliases section within a channel
  const renderAliasesSection = (channelName, aliases, stores) => (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Location Aliases ({aliases.length})
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setAliasDialog({ open: true, mode: 'add', data: {}, store: '' })}
          disabled={stores.length === 0}
        >
          Add Alias
        </Button>
      </Box>
      {aliases.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>Settlement Name</strong></TableCell>
                <TableCell><strong>Maps To (Store)</strong></TableCell>
                <TableCell><strong>Match Type</strong></TableCell>
                <TableCell align="center" width={100}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aliases.map((alias) => (
                <TableRow key={alias.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {alias.settlement_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{alias.canonical_name}</TableCell>
                  <TableCell>
                    <Chip label={alias.match_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setAliasDialog({
                          open: true,
                          mode: 'edit',
                          data: {
                            id: alias.id,
                            settlementName: alias.settlement_name,
                            canonicalName: alias.canonical_name,
                            matchType: alias.match_type,
                            notes: alias.notes
                          },
                          store: alias.canonical_name
                        })}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({
                          open: true,
                          type: 'aliases',
                          id: alias.id,
                          name: alias.settlement_name,
                          channel: channelName
                        })}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          No aliases mapping to stores in this channel.
        </Typography>
      )}
    </Box>
  );

  // Render accounts section for a store/location
  const renderAccountsSection = (storeName) => {
    const accounts = storeAccounts[storeName] || [];

    return (
      <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalance color="primary" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Bank Accounts ({accounts.length})
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={(e) => {
              e.stopPropagation();
              setAccountDialog({ open: true, selectedAccounts: [], store: storeName });
            }}
          >
            Assign Accounts
          </Button>
        </Box>
        {accounts.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell><strong>Account</strong></TableCell>
                  <TableCell align="center" width={60}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{account.account_identifier}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove Account">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({
                              open: true,
                              type: 'location-accounts',
                              id: account.id,
                              name: account.account_identifier,
                              store: storeName
                            });
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
            No accounts assigned to this location.
          </Typography>
        )}
      </Box>
    );
  };

  // Render channel accordion (nested inside organization)
  const renderChannelAccordion = useCallback((channel, orgName) => {
    const key = `${orgName}:${channel.name}`;
    const details = channelDetails[key];
    const isLoading = expandedChannel === key && !details;

    return (
      <Accordion
        key={key}
        expanded={expandedChannel === key}
        onChange={handleAccordionChange(channel.name, orgName)}
        sx={{
          mb: 0.5,
          '&:before': { display: 'none' },
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: expandedChannel === key ? 'secondary.light' : 'grey.100',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">{displayMarket(channel.name)}</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
              <Chip
                label={`${channel.store_count || 0} stores`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`${channel.terminal_count || 0} terminals`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Stack>
            <Box onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Edit Market">
                <IconButton
                  size="small"
                  onClick={() => setChannelDialog({
                    open: true,
                    mode: 'edit',
                    data: { name: channel.name, originalName: channel.name, organization: channel.organization }
                  })}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Market">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteDialog({
                    open: true,
                    type: 'channels',
                    id: channel.name,
                    name: channel.name,
                    channel: ''
                  })}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : details ? (
            <>
              {renderStoresSection(channel.name, details.stores, orgName)}
              <Divider sx={{ my: 2 }} />
              {renderAliasesSection(channel.name, details.aliases, details.stores)}
            </>
          ) : null}
        </AccordionDetails>
      </Accordion>
    );
  }, [channelDetails, expandedChannel, handleAccordionChange, renderStoresSection, renderAliasesSection]);

  // Memoize the heavy accordion content to prevent re-renders when dialog state changes
  const memoizedAccordions = useMemo(() => {
    // Render the union of orgs that have channels (channelsByOrg keys) and ALL known orgs
    // (organizations) so a freshly-created EMPTY org still appears as an accordion the user can
    // build out. Without this merge, an org with only its org-self placeholder row would never
    // become a channelsByOrg key and would be invisible. 'Unassigned' is only shown when it
    // actually holds channels (a real catch-all key in channelsByOrg), never synthesized empty.
    const orgNamesToRender = Array.from(new Set([
      ...Object.keys(channelsByOrg),
      ...organizations.filter(o => o !== 'Unassigned'),
    ]));

    if (orgNamesToRender.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No channels configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Get started by adding your first market.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setChannelDialog({ open: true, mode: 'add', data: {} })}
          >
            Add Market
          </Button>
        </Box>
      );
    }

    return orgNamesToRender
      .sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      })
      .map(orgName => {
        const orgChannels = channelsByOrg[orgName] || [];
        const totalStores = orgChannels.reduce((sum, c) => sum + (parseInt(c.store_count) || 0), 0);
        const totalTerminals = orgChannels.reduce((sum, c) => sum + (parseInt(c.terminal_count) || 0), 0);

        return (
          <Accordion
            key={orgName}
            expanded={expandedOrg === orgName}
            onChange={handleOrgAccordionChange(orgName)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: orgName === 'Unassigned' ? 'warning.light' : 'primary.main',
                color: 'white',
                '& .MuiAccordionSummary-expandIconWrapper': { color: 'white' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" component="span">{orgName}</Typography>
                  {orgName !== 'Unassigned' && (
                    <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 1 }}>
                      <Tooltip title="Edit Channel Name">
                        <IconButton
                          size="small"
                          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                          onClick={() => setEditOrgDialog({ open: true, orgName })}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${orgChannels.length} channels`}
                    size="small"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  <Chip
                    label={`${totalStores} stores`}
                    size="small"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  <Chip
                    label={`${totalTerminals} terminals`}
                    size="small"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1, backgroundColor: 'grey.50' }}>
              {/* Channels */}
              {orgChannels.length > 0 ? (
                orgChannels.map(channel => renderChannelAccordion(channel, orgName))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No channels yet in this organization.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setChannelDialog({ open: true, mode: 'add', data: { organization: orgName } })}
                  >
                    Add Market
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      });
  }, [channelsByOrg, organizations, expandedOrg, handleOrgAccordionChange, renderChannelAccordion, storeAccounts]);

  if (loading && channels.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Channel Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage how terminals are grouped into channels (markets) and stores for reporting. Click a channel to view and edit its stores, aliases, and terminals.
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Box>
            <Button startIcon={<Refresh />} onClick={loadChannels} sx={{ mr: 1 }}>
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setOrgDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Add Channel
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setChannelDialog({ open: true, mode: 'add', data: {} })}
            >
              Add Market
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Channel Hierarchy" />
          <Tab label="Partner Groups" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        {renderSummary()}

        <AssignLocationPanel apiCall={apiCall} onAssigned={loadChannels} />

        <Card>
          <CardContent>
            {memoizedAccordions}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <PartnerGroupsPanel apiCall={apiCall} />
      </TabPanel>

      {/* Channel Dialog */}
      <Dialog open={channelDialog.open} onClose={() => setChannelDialog({ open: false, mode: 'add', data: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{channelDialog.mode === 'add' ? 'Add Market' : 'Edit Market'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Market Name"
            fullWidth
            value={channelDialog.data.name || ''}
            onChange={(e) => setChannelDialog(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Channel</InputLabel>
            <Select
              value={channelDialog.data.organization || ''}
              onChange={(e) => setChannelDialog(prev => ({ ...prev, data: { ...prev.data, organization: e.target.value } }))}
              label="Channel"
            >
              {organizations.map(org => (
                <MenuItem key={org} value={org}>{org}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDialog({ open: false, mode: 'add', data: {} })}>Cancel</Button>
          <Button onClick={handleSaveChannel} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Store Dialog */}
      <Dialog open={storeDialog.open} onClose={() => setStoreDialog({ open: false, mode: 'add', data: {}, channel: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{storeDialog.mode === 'add' ? 'Add Store' : 'Edit Store'}</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            options={allStoreNames}
            value={storeDialog.data.name || ''}
            onChange={(e, value) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, name: value || '' } }))}
            onInputChange={(e, value) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, name: value || '' } }))}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label="Store Name"
                fullWidth
                placeholder="Type to search or enter new store name"
              />
            )}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Channel</InputLabel>
            <Select
              value={storeDialog.data.organization || ''}
              onChange={(e) => setStoreDialog(prev => ({
                ...prev,
                data: { ...prev.data, organization: e.target.value },
                channel: '' // Reset channel when org changes
              }))}
              label="Channel"
            >
              {organizations.map(org => (
                <MenuItem key={org} value={org}>{org}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Market</InputLabel>
            <Select
              value={storeDialog.channel || ''}
              onChange={(e) => setStoreDialog(prev => ({ ...prev, channel: e.target.value }))}
              label="Market"
            >
              {(channelsByOrg[storeDialog.data.organization] || []).map(ch => (
                <MenuItem key={`${storeDialog.data.organization}:${ch.name}`} value={ch.name}>{ch.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            value={storeDialog.data.type || ''}
            onChange={(e) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, type: e.target.value } }))}
            helperText="e.g., Stand-Alone, Semi-Integrated"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreDialog({ open: false, mode: 'add', data: {}, channel: '' })}>Cancel</Button>
          <Button onClick={handleSaveStore} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Alias Dialog */}
      <AliasDialog
        open={aliasDialog.open}
        mode={aliasDialog.mode}
        initial={aliasDialog.data}
        storeOptions={allStoreNames}
        onClose={() => setAliasDialog({ open: false, mode: 'add', data: {}, store: '' })}
        onSave={handleSaveAlias}
      />

      {/* Reassign Terminal Dialog */}
      <Dialog open={terminalDialog.open} onClose={() => setTerminalDialog({ open: false, terminal: null, newStore: '', channel: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign Terminal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Terminal: <strong>{terminalDialog.terminal}</strong>
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>New Store</InputLabel>
            <Select
              value={terminalDialog.newStore}
              onChange={(e) => setTerminalDialog(prev => ({ ...prev, newStore: e.target.value }))}
              label="New Store"
            >
              {allStores.map(store => (
                <MenuItem key={store.id} value={store.name}>
                  {store.name} ({store.channel})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminalDialog({ open: false, terminal: null, newStore: '', channel: '' })}>Cancel</Button>
          <Button onClick={handleReassignTerminal} variant="contained">Reassign</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: '', id: '', name: '', channel: '', store: '' })}>
        <DialogTitle>Confirm {deleteDialog.type === 'location-accounts' ? 'Remove' : 'Delete'}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {deleteDialog.type === 'location-accounts' ? 'remove' : 'delete'} <strong>{deleteDialog.name}</strong>
            {deleteDialog.type === 'location-accounts' && deleteDialog.store && (
              <> from location <strong>{deleteDialog.store}</strong></>
            )}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', id: '', name: '', channel: '', store: '' })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {deleteDialog.type === 'location-accounts' ? 'Remove' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Organization Dialog - isolated component for performance */}
      <AddOrgDialog
        open={orgDialogOpen}
        onClose={() => setOrgDialogOpen(false)}
        onSave={handleSaveOrg}
      />

      {/* Edit Organization Dialog */}
      <EditOrgDialog
        open={editOrgDialog.open}
        orgName={editOrgDialog.orgName}
        onClose={() => setEditOrgDialog({ open: false, orgName: '' })}
        onSave={handleEditOrg}
      />

      {/* Assign Accounts to Store/Location Dialog */}
      <Dialog open={accountDialog.open} onClose={() => setAccountDialog({ open: false, selectedAccounts: [], store: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Accounts to Location
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Location: <strong>{accountDialog.store}</strong>
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={availableAccounts}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.accountName || option.account_name || 'Unknown'} (${option.accountId || option.account_id || option.rta || ''})`;
            }}
            value={accountDialog.selectedAccounts}
            onChange={(event, newValue) => {
              setAccountDialog(prev => ({
                ...prev,
                selectedAccounts: newValue
              }));
            }}
            loading={loadingAvailableAccounts}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Accounts"
                placeholder="Search and select accounts..."
                margin="dense"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAvailableAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.accountId || option.account_id || option.rta}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <ListItemText
                  primary={option.accountName || option.account_name || 'Unknown Account'}
                  secondary={option.accountId || option.account_id || option.rta}
                />
              </li>
            )}
            isOptionEqualToValue={(option, value) =>
              (option.accountId || option.account_id || option.rta) ===
              (value.accountId || value.account_id || value.rta)
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialog({ open: false, selectedAccounts: [], store: '' })}>Cancel</Button>
          <Button onClick={handleSaveStoreAccount} variant="contained" disabled={accountDialog.selectedAccounts.length === 0}>
            Assign ({accountDialog.selectedAccounts.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Channels;
